import { OtsJobConfig } from '../services/otsConfigService';
import { otsResults, llmInsight, CustomerStatus } from '../types/insight';
import { OtsFlowConfig } from '../types/otsConfig';
import { clientWithoutInterceptors } from '../utils/http';

export async function getOtsResultsByJobId(jobId: string): Promise<otsResults> {
  try {
    const response = await clientWithoutInterceptors.get(`/api/ots-results/${jobId}`);

    // if the job is done and there are insights, return them
    if (response.status === 200) {
      const insights = response.data.length > 0
        ? response.data.sort((a: llmInsight, b: llmInsight) => (a.index ?? 0) - (b.index ?? 0))
        : [];
      return {
        insights,
        isLoading: false,
      };
    }

    // if the job is in error, return an empty array to stop polling
    if (response.status === 400) {
      return {
        insights: [],
        isLoading: false,
        error: 'Job failed with 400 status',
      };
    }
    // if the job is still pending, return an empty array to keep polling
    if (response.status === 204) {
      return {
        insights: [],
        isLoading: true,
      };
    }

    return {
      insights: [],
      isLoading: false,
    };
  } catch (error) {
    return {
      insights: [],
      isLoading: false,
      error: 'Job failed with 400 status',
    };
  }
}

export async function getJobConfigById(jobId?:string): Promise<OtsJobConfig> {
  const response = await clientWithoutInterceptors.get(`/api/llm-jobs/${jobId}`);
  if (response.status !== 200) {
    throw new Error('Could not get job config');
  }

  return response.data;
}

export async function getAllOtsFlowConfigs(): Promise<OtsJobConfig[]> {
  const response = await clientWithoutInterceptors.get('/api/llm-jobs');
  if (response.status !== 200) {
    throw new Error('Could not get job configs');
  }

  return response.data;
}

export async function getFlowConfigByFlowId(flowId: string): Promise<OtsFlowConfig> {
  const response = await clientWithoutInterceptors.get(`/api/no-auth/ots-flow-config/${flowId}`);
  if (response.status !== 200) {
    throw new Error('Could not get flow config');
  }
  return response.data;
}

export async function getLatestJobByCustomerIdAndFlowId(flowId: string): Promise<OtsJobConfig> {
  const response = await clientWithoutInterceptors.get(`/api/llm-jobs/latest-by-flow?fid=${flowId}`);
  if (response.status !== 200) {
    throw new Error('Could not get latest job');
  }
  return response.data;
}

export async function getCustomerStatus(): Promise<CustomerStatus> {
  try {
    const response = await clientWithoutInterceptors.get('/api/customer-status');

    if (response.status === 200) {
      return response.data;
    }

    // Return default status if API call fails
    return {
      status: 'start',
      updatedAt: new Date().toISOString(),
    };
  } catch (error) {
    // Return default status on error
    return {
      status: 'start',
      updatedAt: new Date().toISOString(),
    };
  }
}
